// Auth.jsx - Advanced Authentication Page with Login and Signup (Dev/Prod Dual Mode)
import React, { useState, useEffect } from "react";
import Slide01 from "../assets/pictures/Slide_01.png";
import Slide02 from "../assets/pictures/Slide_02.png";
import Slide03 from "../assets/pictures/Slide_03.png";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  CircularProgress,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./Auth.css";

// Import images from assets
import LoginPagePic from "../assets/pictures/LoginPage.png";
import Logo01 from "../assets/images/Logo01.png";

// Create custom theme
const theme = createTheme({
  typography: {
    fontFamily: "'Yekan Bakh FaNum Bold', sans-serif",
  },
  direction: "rtl",
  palette: {
    primary: {
      main: "#344E41",
    },
    secondary: {
      main: "#409435",
    },
  },
});

// Styled components
const AuthContainer = styled("div")(({ theme }) => ({
  display: "flex",
  height: "100vh",
  width: "100vw",
  overflow: "hidden",
  direction: "rtl",
}));

const ImageSection = styled("div", {
  shouldForwardProp: (prop) => prop !== "currentImage",
})(({ theme, currentImage }) => ({
  flex: "1",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#344E41",
  overflow: "hidden",
  zIndex: 0,
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: currentImage ? `url(${currentImage})` : "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
    transition: "background-image 1s ease-in-out",
    zIndex: -1,
  },

  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

// Glass morphism box
const GlassBox = styled("div")(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, 0.31)", //background
  backdropFilter: "blur(14px)",
  borderRadius: "35px",
  padding: theme.spacing(4),
  textAlign: "center",
  color: "white",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  border: "2px solid rgba(255, 255, 255, 0.73)",
  maxWidth: "90%",
  margin: "0 auto",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(16px)",
  },
}));

const ImageContent = styled("div")(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  textAlign: "center",
  color: "white",
  padding: theme.spacing(4),
}));

const FormSection = styled("div")(({ theme }) => ({
  flex: "1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff",
  padding: theme.spacing(4),
  overflowY: "auto",
  [theme.breakpoints.down("md")]: {
    flex: "1 1 100%",
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "450px",
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const LogoContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(3),
}));

const ToggleContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  backgroundColor: "#f5f5f5",
  borderRadius: "12px",
  padding: "4px",
}));

const ToggleButton = styled(Button)(({ theme, active }) => ({
  flex: 1,
  borderRadius: "8px",
  padding: theme.spacing(1.5),
  textTransform: "none",
  fontWeight: "bold",
  fontSize: "1rem",
  backgroundColor: active ? "#344E41" : "transparent",
  color: active ? "#ffffff" : "#666666",
  "&:hover": {
    backgroundColor: active ? "#344E41" : "rgba(52, 78, 65, 0.1)",
  },
  transition: "all 0.3s ease",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f9f9f9",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#344E41",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#344E41",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Yekan Bakh FaNum Bold', sans-serif",
  },
  "& .MuiInputBase-input": {
    fontFamily: "'Yekan Bakh FaNum Bold', sans-serif",
    textAlign: "right",
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(1.5),
  fontSize: "1.1rem",
  fontWeight: "bold",
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(52, 78, 65, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(52, 78, 65, 0.4)",
    transform: "translateY(-2px)",
  },
}));

const Divider = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  margin: theme.spacing(2, 0),
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: "1px",
    backgroundColor: "#e0e0e0",
  },
  "& span": {
    padding: theme.spacing(0, 2),
    color: "#999",
    fontSize: "0.9rem",
  },
}));

const Auth = ({ onLogin }) => {
  const slides = [Slide01, Slide02, Slide03];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Login form state
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  });

  // Signup form state
  const [signupData, setSignupData] = useState({
    fullName: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
  });

  const handleLoginChange = (field) => (e) => {
    setLoginData({ ...loginData, [field]: e.target.value });
    setError("");
  };

  const handleSignupChange = (field) => (e) => {
    setSignupData({ ...signupData, [field]: e.target.value });
    setError("");
  };

  const validateLogin = () => {
    if (!loginData.username.trim()) {
      setError("لطفاً نام کاربری را وارد کنید");
      return false;
    }
    if (!loginData.password) {
      setError("لطفاً رمز عبور را وارد کنید");
      return false;
    }
    if (loginData.password.length < 6) {
      setError("رمز عبور باید حداقل ۶ کاراکتر باشد");
      return false;
    }
    return true;
  };

  const validateSignup = () => {
    if (!signupData.fullName.trim()) {
      setError("لطفاً نام کامل را وارد کنید");
      return false;
    }
    if (!signupData.email.trim()) {
      setError("لطفاً ایمیل را وارد کنید");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) {
      setError("لطفاً یک ایمیل معتبر وارد کنید");
      return false;
    }
    if (!signupData.username.trim()) {
      setError("لطفاً نام کاربری را وارد کنید");
      return false;
    }
    if (signupData.username.length < 4) {
      setError("نام کاربری باید حداقل ۴ کاراکتر باشد");
      return false;
    }
    if (!signupData.password) {
      setError("لطفاً رمز عبور را وارد کنید");
      return false;
    }
    if (signupData.password.length < 6) {
      setError("رمز عبور باید حداقل ۶ کاراکتر باشد");
      return false;
    }
    if (signupData.password !== signupData.confirmPassword) {
      setError("رمز عبور و تکرار آن مطابقت ندارند");
      return false;
    }
    return true;
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/login/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: loginData.username,
          password: loginData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Server responded with an error (401, 400, etc.) — show it, do NOT fall through to onLogin
        setError(data.error || data.detail || "نام کاربری یا رمز عبور اشتباه است");
        return;
      }

      localStorage.setItem("authToken", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      onLogin();
    } catch (err) {
      // Only reaches here on genuine network failure (no connection to server)
      console.warn("API login unreachable, using dev fallback:", err);
      setTimeout(() => onLogin(), 800);
    } finally {
      setLoading(false);
    }
  };

  // Signup handler (unchanged)
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/register/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: signupData.username,
          password: signupData.password,
          email: signupData.email,
          full_name: signupData.fullName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "خطا در ثبت‌نام");
      }

      setIsLogin(true);
      setSignupData({
        fullName: "",
        email: "",
        username: "",
        password: "",
        confirmPassword: "",
      });
      setError("ثبت‌نام با موفقیت انجام شد. لطفاً وارد شوید.");
    } catch (err) {
      console.warn("API signup failed, using dev fallback:", err);
      setIsLogin(true);
      setSignupData({
        fullName: "",
        email: "",
        username: "",
        password: "",
        confirmPassword: "",
      });
      setError("ثبت‌نام با موفقیت انجام شد. لطفاً وارد شوید.");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError("");
    setLoginData({ username: "", password: "" });
    setSignupData({
      fullName: "",
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthContainer>
        {/* Image Section*/}
        <ImageSection currentImage={slides[currentSlide]}>
          {/* content in GlassBox */}
          <GlassBox>
            <img
              src={Logo01}
              alt="Logo"
              style={{ maxWidth: "300px", marginBottom: "2rem" }}
            />

            <Typography variant="body1" sx={{ opacity: 0.95, mt: 1, fontSize: '1.1rem' }}>
              فرادید، سکوی تصمیم‌یار و پایش بهنگام اراضی کشاورزی و منابع آب مبتنی بر تصاویر
              ماهواره‌ای و هوش مصنوعی
            </Typography>
          </GlassBox>
        </ImageSection>

        {/* Form Section */}
        <FormSection>
          <FormContainer>
            <LogoContainer>
              <img
                src={Logo01}
                alt="Logo"
                style={{ maxWidth: "200px", height: "auto" }}
              />
            </LogoContainer>

            <ToggleContainer>
              <ToggleButton
                active={isLogin}
                onClick={() => !isLogin && toggleMode()}
              >
                ورود
              </ToggleButton>
              <ToggleButton
                active={!isLogin}
                onClick={() => isLogin && toggleMode()}
              >
                ثبت‌نام
              </ToggleButton>
            </ToggleContainer>

            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
            >
              {isLogin ? "خوش آمدید" : "ایجاد حساب کاربری"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: "#666", mb: 3 }}
            >
              {isLogin
                ? "لطفاً اطلاعات خود را وارد کنید"
                : "برای شروع، اطلاعات خود را تکمیل کنید"}
            </Typography>

            {error && (
              <Alert
                severity={error.includes("موفقیت") ? "success" : "error"}
                sx={{ borderRadius: "12px" }}
              >
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={isLogin ? handleLogin : handleSignup}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              {!isLogin && (
                <StyledTextField
                  fullWidth
                  label="نام کامل"
                  value={signupData.fullName}
                  onChange={handleSignupChange("fullName")}
                  variant="outlined"
                  required
                />
              )}

              {!isLogin && (
                <StyledTextField
                  fullWidth
                  label="ایمیل"
                  type="email"
                  value={signupData.email}
                  onChange={handleSignupChange("email")}
                  variant="outlined"
                  required
                />
              )}

              <StyledTextField
                fullWidth
                label="نام کاربری"
                value={isLogin ? loginData.username : signupData.username}
                onChange={
                  isLogin
                    ? handleLoginChange("username")
                    : handleSignupChange("username")
                }
                variant="outlined"
                required
                inputProps={{
                  autoComplete: isLogin ? "username" : "username",
                }}
              />

              <StyledTextField
                fullWidth
                label="رمز عبور"
                type={showPassword ? "text" : "password"}
                value={isLogin ? loginData.password : signupData.password}
                onChange={
                  isLogin
                    ? handleLoginChange("password")
                    : handleSignupChange("password")
                }
                variant="outlined"
                required
                inputProps={{
                  autoComplete: isLogin ? "current-password" : "new-password",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "#666" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {!isLogin && (
                <StyledTextField
                  fullWidth
                  label="تکرار رمز عبور"
                  type={showConfirmPassword ? "text" : "password"}
                  value={signupData.confirmPassword}
                  onChange={handleSignupChange("confirmPassword")}
                  variant="outlined"
                  required
                  inputProps={{ autoComplete: "new-password" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          sx={{ color: "#666" }}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {isLogin && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{ color: "#344E41" }}
                      />
                    }
                    label="مرا به خاطر بسپار"
                    sx={{ fontFamily: "'Yekan Bakh FaNum Bold', sans-serif" }}
                  />
                  <Link
                    href="#"
                    sx={{
                      color: "#344E41",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    رمز عبور را فراموش کرده‌اید؟
                  </Link>
                </Box>
              )}

              <SubmitButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : isLogin ? (
                  "ورود"
                ) : (
                  "ثبت‌نام"
                )}
              </SubmitButton>

              {!isLogin && (
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", color: "#666", mt: 2 }}
                >
                  با ثبت‌نام، شما{" "}
                  <Link
                    href="#"
                    sx={{ color: "#344E41", textDecoration: "none" }}
                  >
                    شرایط استفاده
                  </Link>{" "}
                  و{" "}
                  <Link
                    href="#"
                    sx={{ color: "#344E41", textDecoration: "none" }}
                  >
                    حریم خصوصی
                  </Link>{" "}
                  را می‌پذیرید.
                </Typography>
              )}
            </Box>
          </FormContainer>
        </FormSection>
      </AuthContainer>
    </ThemeProvider>
  );
};

export default Auth;
